import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getBookings, getStats } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, CalendarDays, X, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  let cls = "status-badge status-default";
  if (s === "CONFIRMED" || s === "ARRIVED") cls = "status-badge status-confirmed";
  else if (s === "CANCELLED") cls = "status-badge status-cancelled";
  else if (s === "RESERVED") cls = "status-badge status-reserved";
  return <span className={cls} data-testid="booking-status-badge">{status || "N/A"}</span>;
}

export default function BookingList() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [option, setOption] = useState("");
  const [country, setCountry] = useState("");
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    getStats().then((s) => {
      setProducts(s.products || []);
      setCountries(s.countries || []);
    });
  }, []);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      if (dateFrom) params.date_from = format(dateFrom, "yyyy-MM-dd");
      if (dateTo) params.date_to = format(dateTo, "yyyy-MM-dd");
      if (option) params.option = option;
      if (country) params.country = country;
      const data = await getBookings(params);
      setBookings(data.bookings || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo, option, country, page]);

  useEffect(() => {
    const timer = setTimeout(() => fetchBookings(), 300);
    return () => clearTimeout(timer);
  }, [fetchBookings]);

  const clearFilters = () => {
    setSearch(""); setDateFrom(null); setDateTo(null); setOption(""); setCountry(""); setPage(1);
  };

  const hasFilters = search || dateFrom || dateTo || option || country;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Rezervasyonlar</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} rezervasyon bulundu</p>
        </div>
      </div>

      <div className="page-body">
        {/* Filters */}
        <div className="glass-card p-4 mb-4" data-testid="filter-panel">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1 block">Ara</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
                <Input
                  data-testid="search-booking-input"
                  placeholder="Isim, email, telefon..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-8 rounded-lg bg-black/30 border-white/10 text-white placeholder:text-slate-600 text-sm focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1 block">Baslangic</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="filter-date-from" className="rounded-lg border-white/10 bg-black/30 text-sm w-[140px] justify-start font-normal text-slate-300 hover:bg-white/[0.06]">
                    <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                    {dateFrom ? format(dateFrom, "dd.MM.yyyy") : "Tarih sec"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={dateFrom} onSelect={(d) => { setDateFrom(d); setPage(1); }} locale={tr} />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1 block">Bitis</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="filter-date-to" className="rounded-lg border-white/10 bg-black/30 text-sm w-[140px] justify-start font-normal text-slate-300 hover:bg-white/[0.06]">
                    <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                    {dateTo ? format(dateTo, "dd.MM.yyyy") : "Tarih sec"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={dateTo} onSelect={(d) => { setDateTo(d); setPage(1); }} locale={tr} />
                </PopoverContent>
              </Popover>
            </div>

            <div className="min-w-[160px]">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1 block">Urun</label>
              <Select value={option} onValueChange={(v) => { setOption(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="filter-option-select" className="rounded-lg border-white/10 bg-black/30 text-sm text-slate-300">
                  <SelectValue placeholder="Tumu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tumu</SelectItem>
                  {products.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[140px]">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1 block">Ulke</label>
              <Select value={country} onValueChange={(v) => { setCountry(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="filter-country-select" className="rounded-lg border-white/10 bg-black/30 text-sm text-slate-300">
                  <SelectValue placeholder="Tumu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tumu</SelectItem>
                  {countries.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            {hasFilters && (
              <Button variant="outline" data-testid="clear-filters-button" onClick={clearFilters}
                className="rounded-lg border-white/10 bg-white/[0.03] text-slate-400 hover:bg-white/[0.06] text-sm gap-1">
                <X className="w-3.5 h-3.5" /> Temizle
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="glass-card overflow-hidden">
          <table className="w-full booking-table" data-testid="bookings-table">
            <thead>
              <tr><th>Rez. No</th><th>Tarih</th><th>Musteri</th><th>Telefon</th><th>Urun</th><th>Pax</th><th>Ekstralar</th><th>Otel</th><th>Durum</th></tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>{Array.from({ length: 9 }).map((_, j) => (
                    <td key={j}><div className="h-4 bg-white/5 animate-pulse rounded w-full" /></td>
                  ))}</tr>
                ))
              ) : bookings.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-12 text-slate-600">Rezervasyon bulunamadi</td></tr>
              ) : (
                bookings.map((b) => (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => navigate(`/bookings/${b.id}`)}>
                    <td>
                      <span className="font-mono text-xs text-blue-400 font-semibold">{b.confirmation_code || `#${b.bokun_booking_id}`}</span>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="text-sm">{b.booking_date || "N/A"}</div>
                      {b.booking_time && <div className="text-xs text-slate-500">{b.booking_time}</div>}
                    </td>
                    <td>
                      <div className="font-medium text-white text-sm">{b.customer_name || "N/A"}</div>
                      <div className="text-xs text-slate-600 truncate max-w-[180px]">{b.customer_email}</div>
                    </td>
                    <td className="text-xs text-slate-300 whitespace-nowrap">{b.customer_phone || "-"}</td>
                    <td className="max-w-[180px]">
                      <span className="text-sm truncate block">{b.product_name || "N/A"}</span>
                    </td>
                    <td>
                      <span className="font-mono text-blue-400 font-bold">{b.total_pax || 0}</span>
                      <span className="text-[10px] text-slate-600 ml-0.5">
                        ({b.adult_count || 0}Y {b.child_count || 0}C {b.infant_count || 0}B)
                      </span>
                    </td>
                    <td className="text-xs text-purple-300">{b.extras_summary || "-"}</td>
                    <td className="text-xs text-slate-400 max-w-[100px] truncate">{b.hotel_name || "-"}</td>
                    <td><StatusBadge status={b.booking_status} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 px-2" data-testid="pagination">
            <div className="text-xs text-slate-600">Sayfa {page} / {totalPages} ({total} kayit)</div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" data-testid="pagination-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="rounded-lg h-8 w-8 p-0 border-white/10 bg-white/[0.03] text-slate-400 hover:bg-white/[0.06]">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" data-testid="pagination-next"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="rounded-lg h-8 w-8 p-0 border-white/10 bg-white/[0.03] text-slate-400 hover:bg-white/[0.06]">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
